/**
 * CredentialsTable — TanStack Table rendering of credential rows.
 *
 * CR1 (PR #319): card-level expiry indicators per brand guide §23.
 *  - warning (≤30 days): amber border on row + "Expiring in N days — {date}" status chip
 *  - expired (≤0 days): red border on row + "Expired — {date} · Data syncs are paused" status chip
 *  - healthy (>30 days or no expiry): no treatment
 *
 * Epic 3 (Slice 3.2): Status column (Direction A, CEO-chosen).
 *  - Dedicated Status column between Nickname and Actions (§38).
 *  - CredentialStatusBadgeControlled renders the §38 pill + last-tested meta.
 *  - Actions column: state-appropriate action button (Test / Reconnect / Fix access / Try again)
 *    + icon-only Delete (hidden for UserRole.USER).
 *  - Recovery routing: invalid_key → §37e HarvestModal; missing_permissions → §37g panel.
 *
 * Combined-row coherence (OQ-4, AC §"(7)"):
 *  The §23 expiry left-border (amber/red) and the §38 Status badge are orthogonal signals
 *  answering different questions. They render together on CampMinder rows without conflict:
 *  - Expiry border = "key expires on date" (amber = warning, red = expired)
 *  - Status badge = "connection works right now" (green/red/amber/slate/blue)
 *  Design decision: no suppression of either signal. Both render their natural colors.
 *  The two do NOT compete because they occupy different layout regions (left-border vs
 *  dedicated Status column). A row may show amber expiry border + green "Valid" badge
 *  (key expiring but still valid today) — this is informative, not contradictory.
 *  Designer Visual Sign-off required at PR time (OQ-4, CTO-locked 2026-05-30).
 */

import { Fragment, FC, useState, useEffect, useCallback, CSSProperties } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table"
import {
  AlertDialogButtons,
  AlertDialogDescription,
  AlertDialogTitle
} from "@/constants/alert-dialog/alert-dialog"
import { getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { Credential } from "@/types/credentials"
import { AlertDialogButton } from "@/components/custom/AlertDialogButton/AlertDialogButton"
import { useNavigate } from "react-router-dom"
import { ColumnMetaData, TableDataProps } from "@/types/types"
import { TableHeaderComponent } from "@/components/custom/Table/TableHeader"
import { EmptyTableRow } from "@/components/custom/Table/EmptyTableRow"
import { ButtonVariant } from "@/constants/button/button"
import { useSelector, useDispatch } from 'react-redux'
import { AppDispatch, RootState } from "@/redux"
import { deleteCredential, getCredentials } from "@/services/credentialsService"
import { UserRole } from "@/constants/users/userRole"
import { CustomTableCell } from "@/components/custom/Table/TableCell"
import { TableCellType } from "@/constants/table/table"
import { AlertCircle, Clock, Wifi, RefreshCw, ExternalLink, Loader2 } from 'lucide-react'
import {
  computeDaysUntilExpiry,
  formatExpiryDate,
  getExpiryTier,
} from './expiryUtils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CredentialStatusBadgeControlled } from './CredentialStatusBadge'
import { useCredentialStatusProbe } from './useCredentialStatusProbe'
import { HarvestModal } from '@/pages/Dashboards/components/harvest/HarvestModal'
import { RecreateApiKeyInstructionalPanel } from '@/pages/Dashboards/components/creation/RecreateApiKeyInstructionalPanel'
import { startHarvestThunk } from '@/services/HarvestService'
import { dashboardCreationActions, selectHarvestFailure } from '@/redux/slice/dashboardCreationSlice'
import { STATUS_PRESENTATION } from './credentialStatus'

// ─── ExpiryChip ───────────────────────────────────────────────────────────────

interface ExpiryChipProps {
  credential: Credential
}

/**
 * ExpiryChip renders inline below the credential row when tier is warning or expired.
 * Rendered as a full-width colspan cell so it visually sits beneath the row content.
 * Brand guide §23: Per-credential card expiry state.
 */
export const ExpiryChip: FC<ExpiryChipProps> = ({ credential }) => {
  // Gate on type-declared capability: credential types that do not track expiry
  // (e.g. Google Sheets OAuth) must never render an expiry chip, regardless of
  // whatever value apiKeyExpiresAt carries. (Slice 1.4, shared-credential-eligibility TDD §FE)
  if (!credential.tracksExpiry) return null

  const days = computeDaysUntilExpiry(credential.apiKeyExpiresAt)
  const tier = getExpiryTier(days)

  if (tier === 'none') return null

  const dateFormatted = formatExpiryDate(credential.apiKeyExpiresAt)
  const isExpired = tier === 'expired'

  return (
    <div
      data-testid={`expiry-chip-${credential.id}`}
      className={isExpired ? 'text-destructive' : 'text-warning'}
    >
      <Badge variant={isExpired ? 'destructive' : 'warning'}>
        {isExpired ? (
          <AlertCircle size={12} aria-hidden="true" className="flex-shrink-0 mr-2" />
        ) : (
          <Clock size={12} aria-hidden="true" className="flex-shrink-0 mr-2" />
        )}
        {isExpired
          ? `Expired - ${dateFormatted} · Data syncs are paused`
          : `Expiring in ${days} ${days === 1 ? 'day' : 'days'} — ${dateFormatted}`}
      </Badge>
    </div>
  )
}

// ─── Per-row status probe wrapper ─────────────────────────────────────────────

/**
 * CredentialStatusRow — wraps the two cells (Status + Actions) that share live
 * probe state for a single credential row.
 *
 * Because hooks must be called unconditionally and TanStack Table maps cells
 * individually, this component provides a natural per-row scope for the probe
 * state shared between the Status column badge and the Actions column button.
 */
interface CredentialStatusRowProps {
  credential: Credential
  onReconnect: () => void
  onFixAccess: (credential: Credential) => void
  canDelete: boolean
  onDelete: (credentialId: string) => void
}

const CredentialStatusRow: FC<CredentialStatusRowProps> = ({
  credential,
  onReconnect,
  onFixAccess,
  canDelete,
  onDelete,
}) => {
  const { displayState, displayLastTestedAt, isProbing, handleTest } =
    useCredentialStatusProbe(credential.id, credential.connectionStatus)

  const pres = STATUS_PRESENTATION[displayState]

  // Derive action button props per state — single stable DOM node so React keeps
  // focus across the Test → testing transition (§38.8 AC#4, mirrors Card fix).
  const renderActionButton = () => {
    if (credential.connectionStatus === undefined) {
      // Workflow-only — no status action, only delete.
      return null
    }

    // Per-state derivations — computed outside JSX so the single <Button> below
    // is always the same DOM node (no conditional early returns after this point).
    const handleClick = () => {
      if (displayState === 'invalid_key') { onReconnect(); return }
      if (displayState === 'missing_permissions') { onFixAccess(credential); return }
      if (!isProbing) void handleTest()
    }

    const ActionIcon =
      displayState === 'testing'       ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> :
      displayState === 'couldnt_verify' ? <RefreshCw size={14} aria-hidden="true" /> :
      displayState === 'invalid_key'    ? <RefreshCw size={14} aria-hidden="true" /> :
      displayState === 'missing_permissions' ? <ExternalLink size={14} aria-hidden="true" /> :
                                          <Wifi size={14} aria-hidden="true" />

    // §38.3 dynamic AA-safe colors for recovery states (inline for same reason as Card).
    const colorStyle: CSSProperties | undefined =
      displayState === 'invalid_key'
        ? { color: 'hsl(0, 63%, 42%)', borderColor: 'hsl(0, 70%, 90%)' }
        : displayState === 'missing_permissions'
          ? { color: 'hsl(34, 100%, 30%)', borderColor: 'rgba(217, 119, 6, 0.35)' }
          : undefined

    const isRecovery = displayState === 'invalid_key' || displayState === 'missing_permissions'

    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        aria-disabled={isProbing || undefined}
        aria-busy={isProbing || undefined}
        style={colorStyle}
        className={`focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2${
          isProbing ? ' opacity-60 cursor-default' : ''
        }${isRecovery ? ' font-semibold' : ''}`}
        data-testid={`status-action-${credential.id}`}
      >
        {ActionIcon}
        {pres.actionLabel}
      </Button>
    )
  }

  return (
    <>
      {/* Status cell */}
      <TableCell className="w-[300px]" data-testid={`status-cell-col-${credential.id}`}>
        <CredentialStatusBadgeControlled
          credentialId={credential.id}
          displayState={displayState}
          displayLastTestedAt={displayLastTestedAt}
          connectionStatus={credential.connectionStatus}
        />
      </TableCell>

      {/* Actions cell */}
      <TableCell className="w-56" data-testid={`actions-cell-${credential.id}`}>
        <div className="flex items-center gap-2 justify-end">
          {renderActionButton()}
          {canDelete && (
            <AlertDialogButton
              actionButtonText={AlertDialogButtons.DELETE}
              dialogTitle={AlertDialogTitle.DELETE_CREDENTIAL}
              dialogSubtitle={AlertDialogDescription.DELETE_CREDENTIAL}
              onClick={() => onDelete(credential.id)}
              variant={ButtonVariant.DESTRUCTIVE}
              isTableActionButton
            />
          )}
        </div>
      </TableCell>
    </>
  )
}

// ─── Main table component ─────────────────────────────────────────────────────

export const DataTable = ({
  columns,
  data,
}: TableDataProps<Credential>
) => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const { pagination } = useSelector((state: RootState) => state.credentials)
  const harvestFailure = useSelector(selectHarvestFailure)

  // §37e Reconnect modal state (invalid_key recovery — HarvestModal)
  const [isHarvestModalOpen, setIsHarvestModalOpen] = useState(false)

  // §37g Fix access panel state (missing_permissions recovery — RecreateApiKeyInstructionalPanel)
  const [fixAccessCredential, setFixAccessCredential] = useState<Credential | null>(null)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const onDeleteHandler = (credentialId: string) => {
    if (!token) return
    dispatch(deleteCredential(token, credentialId))
      .catch((err: unknown) => { console.log(err) })
  }

  // §37e: Reconnect (invalid_key) — opens the HarvestModal to re-enter/harvest a key.
  // Mirrors the Credentials.tsx handleReconnect pattern for expiry banners.
  // TODO(multi-vendor): the 'campminder' appCode is hardcoded here (and in the
  // RecreateApiKeyInstructionalPanel sourceAppCode + forms/edit.tsx handleReconnect).
  // CampMinder is the only dashboard-era harvest vendor today; when a second vendor
  // ships harvest, derive the appCode from the credential row instead of literals.
  const handleReconnect = useCallback(async () => {
    if (!token) return
    dispatch(dashboardCreationActions.clearHarvest())
    await (dispatch as (action: unknown) => Promise<void>)(
      startHarvestThunk(token, 'campminder')
    )
    setIsHarvestModalOpen(true)
  }, [token, dispatch])

  const handleHarvestClose = useCallback(() => {
    setIsHarvestModalOpen(false)
    dispatch(dashboardCreationActions.clearHarvest())
  }, [dispatch])

  const handleHarvestSuccess = useCallback(() => {
    setIsHarvestModalOpen(false)
    // Refresh the credentials list so the badge reflects the new key's status.
    if (token) {
      void dispatch(getCredentials(token, pagination.limit, pagination.offset))
    }
  }, [token, dispatch, pagination])

  // Close HarvestModal if harvest fails mid-flow (mirrors Credentials.tsx:73-77 useEffect pattern).
  useEffect(() => {
    if (harvestFailure && isHarvestModalOpen) {
      setIsHarvestModalOpen(false)
    }
  }, [harvestFailure, isHarvestModalOpen])

  // §37g: Fix access (missing_permissions) — opens RecreateApiKeyInstructionalPanel.
  const handleFixAccess = useCallback((credential: Credential) => {
    setFixAccessCredential(credential)
  }, [])

  const handleFixAccessClose = useCallback(() => {
    setFixAccessCredential(null)
  }, [])

  const handleFixAccessSuccess = useCallback(() => {
    setFixAccessCredential(null)
    // Refresh the credentials list so the badge reflects the re-entered key's status.
    if (token) {
      void dispatch(getCredentials(token, pagination.limit, pagination.offset))
    }
  }, [token, dispatch, pagination])

  return (
    <>
      <div className="rounded-md border w-full">
        <Table>
          <TableHeaderComponent data={data} columns={columns} />
          <TableBody className="text-primary">
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => {
                const credential = row.original
                // Only compute expiry tier when the credential type declares tracked
                // expiry — same capability gate as ExpiryChip. (Slice 1.4)
                const days = credential.tracksExpiry
                  ? computeDaysUntilExpiry(credential.apiKeyExpiresAt)
                  : null
                const tier = getExpiryTier(days)
                const isWarning = tier === 'warning'
                const isExpired = tier === 'expired'

                const canDelete = userData?.role !== UserRole.USER

                return (
                  <Fragment key={row.id}>
                    <TableRow
                      /* istanbul ignore next -- @preserve no row-selection UI on this table; getIsSelected() is always false */
                      data-state={row.getIsSelected() && "selected"}
                      data-testid={`credential-row-${credential.id}`}
                      className={`bg-primary-foreground hover:bg-border/5 hover:cursor-auto ${
                        isExpired
                          ? 'border-l-2 border-l-destructive'
                          : isWarning
                            ? 'border-l-2 border-l-warning'
                            : ''
                      }`}
                    >
                      {row.getVisibleCells().map((cell) => {
                        // Expiry cell: renders ExpiryChip inline beneath the row.
                        if (cell.column.id === 'expiry') {
                          return (
                            <TableCell key={cell.id} className="w-96">
                              <ExpiryChip credential={credential} />
                            </TableCell>
                          )
                        }

                        // Status + Actions cells share per-row probe state — rendered
                        // together by CredentialStatusRow to avoid stale closure issues.
                        if (cell.column.id === 'connectionStatus') {
                          return (
                            <CredentialStatusRow
                              key={`${cell.id}-status-actions`}
                              credential={credential}
                              onReconnect={() => { void handleReconnect() }}
                              onFixAccess={handleFixAccess}
                              canDelete={canDelete}
                              onDelete={onDeleteHandler}
                            />
                          )
                        }

                        // Skip the actions cell — rendered inside CredentialStatusRow above.
                        if (cell.column.id === 'actions') {
                          return null
                        }

                        return (
                          <CustomTableCell
                            key={cell.id}
                            cell={cell}
                            onClick={() => {
                              if ((cell.column.columnDef.meta as ColumnMetaData)?.type === TableCellType.LINK_BUTTON) {
                                navigate(`/credentials/edit/${cell.getContext().row.original.id}`)
                              }
                            }}
                          />
                        )
                      })}
                    </TableRow>
                  </Fragment>
                )
              })
            ) : (
              <EmptyTableRow numberOfColumns={columns.length} />
            )}
          </TableBody>
        </Table>
      </div>

      {/* §37e: HarvestModal for Reconnect (invalid_key recovery) */}
      <HarvestModal
        isOpen={isHarvestModalOpen}
        onClose={handleHarvestClose}
        onSuccess={handleHarvestSuccess}
        onPasteKey={handleHarvestClose}
        advanceCreationFlowOnSuccess={false}
      />

      {/* §37g: RecreateApiKeyInstructionalPanel for Fix access (missing_permissions recovery) */}
      {fixAccessCredential && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label="Fix credential access"
        >
          <div className="bg-card rounded-xl shadow-2xl w-[380px] max-h-[90vh] overflow-y-auto">
            <RecreateApiKeyInstructionalPanel
              credentialId={fixAccessCredential.id}
              credentialLabel={fixAccessCredential.nickname}
              sourceAppCode="campminder"
              onClose={handleFixAccessClose}
              onSuccess={handleFixAccessSuccess}
            />
          </div>
        </div>
      )}
    </>
  )
}
